"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { SpectrumBar } from "@/components/tools/physics-waves/spectrum-bar";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  AVOGADRO,
  type BandInfo,
  classify,
  ELEMENTARY_CHARGE,
  type PhotonInput,
  type PhotonValues,
  photonFrom,
  wavelengthToRgb,
} from "@/lib/tool-kit/physics-waves/electromagnetic";
import {
  formatSig,
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  findUnit,
  prefixUnits,
  toBase,
  type UnitDef,
} from "@/lib/tool-kit/physics-waves/units";

/** English strings. A translated content file overrides these through `ui`. */
export const WAVELENGTH_FREQUENCY_CALCULATOR_STRINGS = {
  kindLabel: "What do you know?",
  kinds: {
    wavelength: "Wavelength",
    frequency: "Frequency",
    energy: "Photon energy",
    wavenumber: "Wavenumber",
  },
  valueLabels: {
    wavelength: "Wavelength (in vacuum)",
    frequency: "Frequency",
    energy: "Photon energy",
    wavenumber: "Wavenumber",
  },
  unitFor: "Unit for {name}",
  sigFigs: "Round to this many significant figures",
  tryLabel: "Try an example",
  presets: {
    green: "Green laser pointer (532 nm)",
    helium: "Helium–neon laser (632.8 nm)",
    uvc: "UV-C lamp (254 nm)",
    co2: "CO₂ laser (10.6 µm)",
    wifi: "Wi-Fi (2.4 GHz)",
    fm: "FM radio (100 MHz)",
    hydrogen: "Hydrogen 21 cm line",
    xray: "Diagnostic X-ray (60 keV)",
  },
  results: "Results",
  enterValue: "Enter a number above zero to see the conversions.",
  needPositive: "Enter a number above zero.",
  wavelength: "Wavelength",
  frequency: "Frequency",
  energyEv: "Photon energy",
  energyJ: "Photon energy in joules",
  energyMol: "Energy per mole of photons",
  wavenumber: "Wavenumber",
  band: "Band",
  bandLine: "{band} — {detail}",
  bandOnly: "{band}",
  bandNames: {
    gamma: "Gamma rays",
    xray: "X-rays",
    ultraviolet: "Ultraviolet",
    visible: "Visible",
    infrared: "Infrared",
    microwave: "Microwave",
    radio: "Radio",
  },
  subNames: {
    uvc: "UV-C",
    uvb: "UV-B",
    uva: "UV-A",
    violet: "violet",
    blue: "blue",
    green: "green",
    yellow: "yellow",
    orange: "orange",
    red: "red",
    nearIr: "near infrared",
    midIr: "mid infrared",
    farIr: "far infrared",
  },
  ituDetail: "ITU {band} band",
  working: "The working",
  stepWavelengthFromFrequency: "λ = c ÷ f = {c} ÷ {f} = {lambda}",
  stepWavelengthFromEnergy: "λ = h × c ÷ E = {h} × {c} ÷ {e} = {lambda}",
  stepWavelengthFromWavenumber: "λ = 1 ÷ ν̃ = 1 ÷ {k} = {lambda}",
  stepFrequency: "f = c ÷ λ = {c} ÷ {lambda} = {f}",
  stepEnergy: "E = h × f = {h} × {f} = {e}",
  stepElectronvolts: "E = {e} ÷ {q} = {ev}",
  constants:
    "Uses the exact SI values c = 299 792 458 m/s, h = 6.626 070 15 × 10⁻³⁴ J·s, e = 1.602 176 634 × 10⁻¹⁹ C and N = 6.022 140 76 × 10²³ per mole. Wavelengths are in vacuum.",
  swatchNote: "Approximate on-screen colour",
  figureLabel: "The electromagnetic spectrum on a logarithmic wavelength scale",
  figureDescription:
    "Bands from gamma rays on the left to radio waves on the right, with a marker showing where this wavelength falls.",
  copy: "Copy results",
  copied: "Copied!",
  copyText: "λ = {lambda}; f = {f}; E = {ev} ({j}); ν̃ = {k}",
} as const;

type Strings = typeof WAVELENGTH_FREQUENCY_CALCULATOR_STRINGS;
type PresetKey = keyof Strings["presets"];

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const H_TEXT = "6.62607015 × 10⁻³⁴ J·s";
const C_TEXT = "299 792 458 m/s";
const E_TEXT = "1.602176634 × 10⁻¹⁹ J/eV";

const eV = (symbol: string, exp: number): UnitDef => ({
  id: symbol,
  symbol,
  exp,
  factor: ELEMENTARY_CHARGE,
});

const UNITS: Record<PhotonInput, UnitDef[]> = {
  wavelength: [
    ...prefixUnits("m", ["p"]),
    { id: "A", symbol: "Å", exp: -10 },
    ...prefixUnits("m", ["n", "µ", "m", "", "k"]),
  ],
  frequency: prefixUnits("Hz", ["", "k", "M", "G", "T", "P", "E"]),
  energy: [
    eV("µeV", -6),
    eV("meV", -3),
    eV("eV", 0),
    eV("keV", 3),
    eV("MeV", 6),
    eV("GeV", 9),
    { id: "J", symbol: "J", exp: 0 },
    { id: "kJ/mol", symbol: "kJ/mol", exp: 3, factor: 1 / AVOGADRO },
  ],
  wavenumber: [
    { id: "1/cm", symbol: "cm⁻¹", exp: 0, factor: 100 },
    { id: "1/m", symbol: "m⁻¹", exp: 0 },
    { id: "1/um", symbol: "µm⁻¹", exp: 6 },
  ],
};

const PRESETS: Record<
  PresetKey,
  { kind: PhotonInput; value: string; unit: string }
> = {
  green: { kind: "wavelength", value: "532", unit: "nm" },
  helium: { kind: "wavelength", value: "632.8", unit: "nm" },
  uvc: { kind: "wavelength", value: "254", unit: "nm" },
  co2: { kind: "wavelength", value: "10.6", unit: "um" },
  wifi: { kind: "frequency", value: "2.4", unit: "GHz" },
  fm: { kind: "frequency", value: "100", unit: "MHz" },
  hydrogen: { kind: "frequency", value: "1420.405751768", unit: "MHz" },
  xray: { kind: "energy", value: "60", unit: "keV" },
};

function bandText(info: BandInfo, strings: Strings): string {
  const band = strings.bandNames[info.band];
  if (info.itu !== null) {
    return t(strings.bandLine, {
      band,
      detail: t(strings.ituDetail, { band: info.itu }),
    });
  }
  if (info.sub !== null) {
    return t(strings.bandLine, { band, detail: strings.subNames[info.sub] });
  }
  return t(strings.bandOnly, { band });
}

function workingLines(
  kind: PhotonInput,
  p: PhotonValues,
  fmt: NumberFormatter,
  sig: number,
  strings: Strings
): string[] {
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const text = {
    c: C_TEXT,
    h: H_TEXT,
    q: E_TEXT,
    lambda: show(p.wavelength, "m"),
    f: show(p.frequency, "Hz"),
    e: `${formatSig(fmt, p.joules, sig)} J`,
    ev: show(p.electronvolts, "eV"),
    k: `${formatSig(fmt, p.wavenumber / 100, sig)} cm⁻¹`,
  };
  const lines: string[] = [];
  if (kind === "frequency") {
    lines.push(t(strings.stepWavelengthFromFrequency, text));
  } else if (kind === "energy") {
    lines.push(t(strings.stepWavelengthFromEnergy, text));
  } else if (kind === "wavenumber") {
    lines.push(t(strings.stepWavelengthFromWavenumber, text));
  }
  lines.push(
    t(strings.stepFrequency, text),
    t(strings.stepEnergy, text),
    t(strings.stepElectronvolts, text)
  );
  return lines;
}

function Results({
  kind,
  p,
  strings,
  fmt,
  sig,
}: {
  kind: PhotonInput;
  p: PhotonValues;
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const swatch = wavelengthToRgb(p.wavelength * 1e9);
  const wavenumber = `${formatSig(fmt, p.wavenumber / 100, sig)} cm⁻¹`;
  const joules = `${formatSig(fmt, p.joules, sig)} J`;
  const moles = `${formatSig(fmt, p.kilojoulesPerMole, sig)} kJ/mol`;
  const info = classify(p.wavelength);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis={kind !== "wavelength"}
          label={strings.wavelength}
          value={show(p.wavelength, "m")}
        />
        <StatCard
          emphasis={kind !== "frequency"}
          label={strings.frequency}
          value={show(p.frequency, "Hz")}
        />
        <StatCard
          emphasis={kind !== "energy"}
          label={strings.energyEv}
          value={show(p.electronvolts, "eV")}
        />
        <StatCard label={strings.energyJ} value={joules} />
        <StatCard label={strings.energyMol} value={moles} />
        <StatCard
          emphasis={kind !== "wavenumber"}
          label={strings.wavenumber}
          value={wavenumber}
        />
      </StatGrid>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-4">
        {swatch === null ? null : (
          <span
            aria-hidden="true"
            className="size-8 rounded-full border border-border"
            style={{ backgroundColor: swatch }}
            title={strings.swatchNote}
          />
        )}
        <div>
          <p className="text-muted-foreground text-xs">{strings.band}</p>
          <p className="font-semibold text-foreground">
            {bandText(info, strings)}
          </p>
        </div>
      </div>
      <SpectrumBar strings={strings} wavelength={p.wavelength} />
      <WorkingSteps
        heading={strings.working}
        lines={workingLines(kind, p, fmt, sig, strings)}
      />
      <p className="text-muted-foreground text-xs">{strings.constants}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          lambda: show(p.wavelength, "m"),
          f: show(p.frequency, "Hz"),
          ev: show(p.electronvolts, "eV"),
          j: joules,
          k: wavenumber,
        })}
      />
    </>
  );
}

export default function WavelengthFrequencyCalculatorWidget() {
  const strings = useToolUi(WAVELENGTH_FREQUENCY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [kind, setKind] = useState<PhotonInput>("wavelength");
  const [values, setValues] = useState<
    Record<PhotonInput, { value: string; unit: string }>
  >({
    wavelength: { value: "532", unit: "nm" },
    frequency: { value: "100", unit: "MHz" },
    energy: { value: "2.33", unit: "eV" },
    wavenumber: { value: "18797", unit: "1/cm" },
  });
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const sig = Number(sigText);
  const entry = values[kind];
  const typed = parseNumberInput(entry.value);
  const base =
    typed === null ? null : toBase(typed, findUnit(UNITS[kind], entry.unit));
  const photon = base === null ? null : photonFrom(kind, base);

  const applyPreset = (key: PresetKey) => {
    const preset = PRESETS[key];
    setKind(preset.kind);
    setValues((current) => ({
      ...current,
      [preset.kind]: { value: preset.value, unit: preset.unit },
    }));
  };

  return (
    <>
      <PillGroup
        label={strings.kindLabel}
        name="wf-kind"
        onChange={setKind}
        options={(Object.keys(strings.kinds) as PhotonInput[]).map((value) => ({
          value,
          label: strings.kinds[value],
        }))}
        value={kind}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          id="wf-value"
          label={strings.valueLabels[kind]}
          onChange={(value) =>
            setValues({ ...values, [kind]: { ...entry, value } })
          }
          onUnitChange={(unit) =>
            setValues({ ...values, [kind]: { ...entry, unit } })
          }
          unit={entry.unit}
          unitLabel={t(strings.unitFor, { name: strings.valueLabels[kind] })}
          units={UNITS[kind]}
          value={entry.value}
        />
        <SelectField
          id="wf-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {(Object.keys(PRESETS) as PresetKey[]).map((key) => (
          <button
            className={BUTTON_SMALL}
            key={key}
            onClick={() => applyPreset(key)}
            type="button"
          >
            {strings.presets[key]}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typed === null ? <Notice>{strings.enterValue}</Notice> : null}
        {typed !== null && photon === null ? (
          <ErrorNotice>{strings.needPositive}</ErrorNotice>
        ) : null}
        {photon === null ? null : (
          <Results
            fmt={fmt}
            kind={kind}
            p={photon}
            sig={sig}
            strings={strings}
          />
        )}
      </section>
    </>
  );
}
